import { useEffect, forwardRef, useRef } from 'react'
import type { ForwardedRef, InputHTMLAttributes } from 'react'
import tw, { styled } from 'twin.macro'

import { useForkRef } from '../../hooks'
import { focusStyles } from '../sharedStyles'

const StyledCheckbox = styled.input`
  ${focusStyles}

  ${tw`
    w-4
    h-4
    appearance-none
    p-0
    select-none
    flex-shrink-0
    inline-block
    bg-default-subdued
    border
    border-solid
    border-gray-300
    align-top
    rounded
    cursor-pointer
  `}

  &:checked {
    ${tw`
      bg-brand-emphasis
      border-brand-emphasis
    `}
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3E%3C/svg%3E");
  }

  &:indeterminate {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 16 16'%3E%3Cpath stroke='%231874FF' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 8h8'/%3E%3C/svg%3E");
  }

  &:disabled {
    ${tw`bg-default-neutral pointer-events-none`}
  }
`

// InputHTMLAttributes doesn't support indeterminate as it is not
//  a valid _attribute_ and exists only throught JS implementation.
type CheckboxAttributes = InputHTMLAttributes<HTMLInputElement> & {
  indeterminate?: boolean
}

type Props = CheckboxAttributes

function Checkbox(props: Props, ref: ForwardedRef<HTMLInputElement>) {
  const innerRef = useRef<HTMLInputElement>(null)
  const forkedRef = useForkRef(ref, innerRef)

  useEffect(() => {
    if (innerRef.current) {
      innerRef.current.indeterminate = Boolean(props.indeterminate)
    }
  }, [innerRef, props.indeterminate])

  return <StyledCheckbox type="checkbox" ref={forkedRef} {...props} />
}

export default forwardRef(Checkbox)
